import { useEffect, useState } from "react";
import DataTableBase from "../../common/Datatable";
import moment from "moment";
import { Link, useParams, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import { enumToString, isAccessAllowed } from "../../../../utils/helpers";
import _ from "lodash";
import makeRequest from "../../../../api";
import {
  assessmentService,
  subscriptionService,
  userService,
} from "../../../../api/services";
import { toast } from "react-toastify";

const ReportListingPage = () => {
  const params = useParams();
  const userId = params.userId;

  const [subscription, setSubscription] = useState(useLocation().state);
  const [reports, setReports] = useState<any>([]);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [activePlans, setActivePlans] = useState<any>([]);
  const [isCohortCreated, setIsCohortCreated] = useState(false);

  const isReportAddAllowed = isAccessAllowed("REPORT_ADD");
  const isApprovePlanGenerationButtonAllowed = isAccessAllowed(
    "APPROVE_PLAN_GENRATION_BUTTON"
  );
  const isPrescriptionViewAllowed = isAccessAllowed("PRESCRIPTION_VIEW");
  const isGeneratePlanButtonAllowed = isAccessAllowed("GENERATE_PLAN_BUTTON");

  const fetchReports = async () => {
    try {
      const res = await makeRequest(
        assessmentService.getUserReportsList(userId)
      );
      const response = res?.data?.body?.reportsList?.sort(
        (a: any, b: any) => +b?.reportGeneratedAt - +a?.reportGeneratedAt
      );
      setReports([...response]);
    } catch (error: any) {
      toast.error("Error fetching reports list!");
    }
  };

  const getUserData = async () => {
    try {
      const res = await makeRequest(userService.getUserDetails(userId));
      setUserDetails({ ...res?.data?.userDto });
    } catch (error: any) {
      toast.error("Error fetching user details!");
    }
  };

  const getCurrentActive = async () => {
    try {
      const res = await makeRequest(
        subscriptionService.getUserCurrentActive(userId)
      );
      setActivePlans([...res?.data?.data]);
    } catch (error: any) {
      toast.error("Error fetching user's current active plans!");
    }
  };

  const getUserStatus = async (vertical: string) => {
    try {
      const res = await makeRequest(
        userService.getUserState({ vertical: vertical }, userId)
      );
      const state = res?.data?.body?.latestState || undefined;
      setIsCohortCreated(state === "COHORT_CREATED");
    } catch (error: any) {
      toast.error("Error fetching user states");
    }
  };

  useEffect(() => {
    if (userId) {
      getUserData();
      fetchReports();
      getCurrentActive();
    }
  }, [userId]);

  useEffect(() => {
    if (userId && Array.isArray(activePlans) && activePlans?.length > 0) {
      const vertical = activePlans?.[0]?.vertical;
      getUserStatus(vertical);
    }
  }, [userId, activePlans]);

  const columns = [
    {
      name: "File name",
      selector: (row: any) => row.subtitle,
      cell: (row: any) => {
        return (
          <Link to={`${row.reportId}`} state={subscription}>
            {row?.subtitle?.substring(0, 25)}...
          </Link>
        );
      },
    },
    {
      name: "Source",
      selector: (row: any) => row?.fileDetails?.initiationPoint,
      cell: (row: any) => {
        return <div>{enumToString(row?.fileDetails?.initiationPoint)}</div>;
      },
    },
    {
      name: "Health issue Type",
      selector: (row: any) => enumToString(row.healthIssueType),
    },
    {
      name: "Report Date",
      selector: (row: any) => row.reportGeneratedAt,
      format: (row: any) =>
        moment(row.reportGeneratedAt).format("DD MMM YYYY, hh:mm A"),
      sortable: true,
    },
    {
      name: "Document Type",
      selector: (row: any) => enumToString(row.documentType),
    },
  ];

  const generatePlans = async () => {
    try {
      if (!_.isUndefined(activePlans) && activePlans.length > 0) {
        let date = moment().format("YYYY-MM-DD");

        const subscriptionData = await makeRequest(
          subscriptionService.changeSubscriptionDate(
            {
              sortKey: activePlans[0]?.sk,
              newDate: date,
            },
            userId
          )
        );
        if (subscriptionData?.data?.status === 200) {
          setTimeout(() => {
            fetchSubscription();
          }, 1000);
        }
      }
      toast.success("Plan generated successfully!");
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    }
  };

  const updateSubscriptionForDoctorApproval = async () => {
    try {
      if (activePlans.length > 0) {
        for (let i = 0; i < activePlans.length; ++i) {
          await makeRequest(
            subscriptionService.updateSubscription(
              {
                sortKey: activePlans[i]?.sk,
                updates: [
                  {
                    attributeName: "issueBasedMealPlanStatus",
                    attributeValue: "APPROVED_FROM_DOCTOR",
                  },
                ],
              },
              userId
            )
          );
        }
        fetchSubscription();
      }
      toast.success("Approved for plan generation.");
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    }
  };

  const isPlanToBeApproved = () => {
    let status = false;
    if (
      !_.isUndefined(subscription) &&
      !_.isNull(subscription) &&
      subscription?.issueBasedMealPlanStatus !== "APPROVED_FROM_DOCTOR" &&
      subscription?.issueBasedMealPlanStatus !== "MEAL_PLAN_GENERATED"
    ) {
      status = true;
    }
    return status;
  };

  const isPlanGenerationPending = () => {
    let status = false;
    if (
      !_.isUndefined(subscription) &&
      !_.isNull(subscription) &&
      subscription?.issueBasedMealPlanStatus === "APPROVED_FROM_DOCTOR"
    ) {
      status = true;
    }
    return status;
  };

  const fetchSubscription = async () => {
    try {
      const res = await makeRequest(
        subscriptionService.getUserCurrentActive(userId)
      );
      const nutritionSubscription = res?.data?.data?.find(
        (item: any) => item?.gabit === "NUTRITION"
      );
      if (nutritionSubscription.length > 0) {
        setSubscription(nutritionSubscription);
      }
    } catch (error: any) {
      toast.error("Error fetching subscritpion details!");
    }
  };

  return (
    <div className="container-lg">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb mb-3">
          <li className="breadcrumb-item">
            <Link to="/">Home</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Reports
          </li>
        </ol>
      </nav>
      <div className="d-flex justify-content-between align-items-center">
        {userDetails?.fullName && (
          <h6 className="fw-700 fs-24 mb-0">
            {userDetails?.fullName}'s reports
          </h6>
        )}
        <div>
          {isPlanGenerationPending() && isGeneratePlanButtonAllowed && (
            <Button
              className="btn btn-sm me-3"
              onClick={generatePlans}
              disabled={!isCohortCreated}
            >
              Generate plan
            </Button>
          )}
          {isPlanToBeApproved() && isApprovePlanGenerationButtonAllowed && (
            <Button
              className="btn btn-sm me-3"
              onClick={updateSubscriptionForDoctorApproval}
              disabled={reports?.length === 0}
            >
              Approve plan generation
            </Button>
          )}
          <Link
            to={`/users/${userId}/reports/metrics`}
            state={subscription}
            className="btn btn-sm btn-outline-secondary me-3"
          >
            Health metrics
          </Link>
          {isPrescriptionViewAllowed && (
            <Link
              to={`/users/${userId}/prescriptions`}
              state={subscription}
              className="btn btn-sm btn-outline-secondary me-3"
            >
              Prescriptions
            </Link>
          )}
          {isReportAddAllowed && (
            <Link
              to="add"
              state={subscription}
              className="btn btn-sm btn-outline-success"
            >
              Add report
            </Link>
          )}
        </div>
      </div>
      <div style={{ marginTop: "16px" }}>
        <DataTableBase columns={columns} data={reports} />
      </div>
    </div>
  );
};

export default ReportListingPage;
